import {
  getImpersonationExpiresAt,
  getImpersonationProfile,
  isImpersonationActive,
} from "@healthos/auth/react";
import { useEffect, useState } from "react";

function impersonationDisplayName(): string {
  const profile = getImpersonationProfile();
  return profile?.name || profile?.email || "a member";
}

/** Whole minutes left until the token expires, or null when no expiry is known. */
function minutesRemaining(expiresAt: number | null, now: number): number | null {
  if (expiresAt === null) return null;
  return Math.max(0, Math.ceil((expiresAt * 1000 - now) / 60_000));
}

function countdownLabel(minutes: number | null): string {
  if (minutes === null) return " (read-only)";
  if (minutes <= 0) return " (read-only). Session ended.";
  if (minutes === 1) return " (read-only). Session ends in under a minute.";
  return ` (read-only). Session ends in ${minutes} min.`;
}

/**
 * Persistent "Viewing as X" indicator for a support "Log in as user"
 * impersonation session, with a live countdown to the 15-minute
 * token expiry. Mounted in app.tsx; renders nothing outside an active
 * impersonation session. `sticky` (not `fixed`) so it occupies layout space at
 * the top and does not overlay the app header; z-[80] keeps it above the
 * sticky side-nav (z-[70]).
 */
export function ImpersonationBanner() {
  const expiresAt = getImpersonationExpiresAt();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (expiresAt === null) return;
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, [expiresAt]);

  if (!isImpersonationActive()) return null;
  const minutes = minutesRemaining(expiresAt, now);
  return (
    <div
      role="status"
      className="sticky inset-x-0 top-0 z-[80] bg-navy px-4 py-2 text-center text-sm text-cream"
    >
      Viewing as {impersonationDisplayName()}
      {countdownLabel(minutes)}
    </div>
  );
}
